// My Desk — SPEC-001 §12, R-7 ("My Desk", never "My Drive"). Per-manager
// private folder view over drive_file. R-6: DIRECTOR is excluded — enforced
// server-side by GET /api/v1/my-desk (src/company.js), which this page
// simply reflects. A DIRECTOR who reaches this page some other way (the nav
// already hides the link, per lib/nav.ts's showMyDesk) still gets the same
// 403 the endpoint returns to anyone calling it directly, rendered as a
// named fault rather than a blank (INV-9) — never a silent empty list, which
// would be indistinguishable from "no files yet".
import type { JSX } from 'preact';
import { authedGet } from '../lib/apiClient';
import { FaultState, EmptyState } from '../components/FaultState';
import { formatDate } from '../lib/date';
import { useResource } from '../lib/store';

const MY_DESK_KEY = 'my-desk';

export interface DeskFile {
  id: number;
  file_name: string;
  mime_type: string | null;
  url: string | null;
  indexed_at: string;
}

interface MyDeskResponse {
  files: DeskFile[];
}

type Load =
  | { status: 'loading' }
  | { status: 'ready'; files: DeskFile[] }
  | { status: 'refused' }
  | { status: 'fault'; error: string };

export function MyDeskPage(): JSX.Element {
  // F-44: cached resource read, not component-local state — see People.tsx
  // for the full rationale. Navigating away and back reads memory (INV-1).
  const resource = useResource<MyDeskResponse>(MY_DESK_KEY, () => authedGet('/api/v1/my-desk'));

  // The server refuses DIRECTOR (and anyone else outside OWNER/MANAGER) with
  // a 403 that carries no `files` key — that is not a load failure, it is
  // the access rule working. Named separately from a genuine fault so the
  // two are never confused in the UI (INV-9).
  let load: Load;
  if (resource.status === 'ready') {
    load = { status: 'ready', files: resource.data.files };
  } else if (resource.status === 'fault') {
    load = /no access/i.test(resource.error) ? { status: 'refused' } : { status: 'fault', error: resource.error };
  } else {
    load = { status: 'loading' };
  }

  if (load.status === 'loading') {
    return (
      <div class="main">
        <div class="page-header">
          <h1>My Desk</h1>
        </div>
        <EmptyState label="Loading My Desk…" />
      </div>
    );
  }

  if (load.status === 'refused') {
    return (
      <div class="main">
        <div class="page-header">
          <h1>My Desk</h1>
        </div>
        <FaultState name="my_desk_refused" detail="This account does not have a My Desk (R-6)." />
      </div>
    );
  }

  if (load.status === 'fault') {
    return (
      <div class="main">
        <div class="page-header">
          <h1>My Desk</h1>
        </div>
        <FaultState name="my_desk_load_failed" detail={load.error} />
      </div>
    );
  }

  return (
    <div class="main">
      <div class="page-header">
        <h1>My Desk</h1>
      </div>
      {load.files.length === 0 ? (
        <EmptyState label="Nothing in My Desk yet." />
      ) : (
        <table class="my-desk-files" data-testid="my-desk-files">
          <tbody>
            {load.files.map((f) => (
              <tr key={f.id} data-testid={`my-desk-file-${f.id}`}>
                <td>{f.url ? <a class="doc-link" href={f.url} target="_blank" rel="noreferrer">{f.file_name}</a> : f.file_name}</td>
                <td>{formatDate(f.indexed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
